interface Point {
  date: string;
  value: number;
}

/**
 * A small, dependency-free SVG trend chart. Draws a smoothed line with a soft
 * area fill, optional faint raw points, and end-value labels. Scales to its
 * container via viewBox.
 */
export function TrendChart({
  line,
  raw,
  unit = "",
  height = 120,
  color = "var(--accent)"
}: {
  line: Point[];
  raw?: Point[];
  unit?: string;
  height?: number;
  color?: string;
}) {
  const W = 320;
  const H = height;
  const padX = 8;
  const padY = 16;

  if (line.length === 0) {
    return <div className="dim text-sm center" style={{ padding: "var(--space-6)" }}>No data yet.</div>;
  }

  const all = [...line, ...(raw ?? [])];
  const values = all.map((p) => p.value);
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const range = max - min;
  min -= range * 0.12;
  max += range * 0.12;

  const n = line.length;
  const x = (i: number, count: number) =>
    count <= 1 ? W / 2 : padX + (i / (count - 1)) * (W - padX * 2);
  const y = (v: number) => padY + (1 - (v - min) / (max - min)) * (H - padY * 2);

  const linePath = line
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i, n).toFixed(1)} ${y(p.value).toFixed(1)}`)
    .join(" ");
  const areaPath =
    `${linePath} L ${x(n - 1, n).toFixed(1)} ${(H - padY).toFixed(1)} L ${x(0, n).toFixed(1)} ${(H - padY).toFixed(1)} Z`;

  const first = line[0]!;
  const last = line[line.length - 1]!;
  const gradId = `g${Math.random().toString(36).slice(2, 8)}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {raw?.map((p, i) => {
        // Place raw dots by matching index position across the same count.
        const idx = line.findIndex((l) => l.date === p.date);
        const xi = idx >= 0 ? x(idx, n) : x(i, raw.length);
        return <circle key={`r${i}`} cx={xi} cy={y(p.value)} r={2} fill="var(--text-tertiary)" opacity={0.5} />;
      })}
      <circle cx={x(n - 1, n)} cy={y(last.value)} r={3.5} fill={color} />
      <text x={padX} y={y(first.value) - 6} fontSize={11} fill="var(--text-tertiary)">
        {first.value}
        {unit}
      </text>
      <text x={W - padX} y={y(last.value) - 6} fontSize={11} fill="var(--text-primary)" textAnchor="end" fontWeight={600}>
        {last.value}
        {unit}
      </text>
    </svg>
  );
}
